import {
  drawGuides,
  getAnchorPoints,
  getGuides,
  getLineGuideStops,
} from 'dataloader/utils/guideLines'
import Konva from 'konva'
import { round } from 'lodash'
import React, { useState, useEffect } from 'react'
import { Group, Line, Rect } from 'react-konva'
import { connect } from 'react-redux'
import action from 'store/actions'
import { incrementMaterialPresets } from 'store/materialPresets/actions'
import {
  getAutoIncrementPresetsEnabled,
  getDescriptions,
  getDescriptionsData,
  getDrawings,
  getMaterialTypePresets,
  getSnappingDistance,
  getSnappingEnabled,
} from 'store/selectors'
import randomId from 'utils/randomId'
import { throttleRaf } from 'utils/throttleRaf'
import withSelectedValues, { DataloaderInfo } from 'utils/withSelectedValues'

interface OwnProps {
  width: number
  height: number
  scale: { x: number; y: number }
}

interface StateProps {
  descriptions: string[]
  descriptionsData: ReduxStore.Materials.Data.DescriptionData[]
  drawings: ReduxStore.Drawings.Data.Drawing[]
  snappingEnabled: boolean
  presets: ReduxStore.MaterialPresets.MaterialPreset[]
  autoIncrementEnabled: boolean
  snappingDistance: number
  options: any
}

interface DispatchProps {
  dispatchAddMaterials: (m: ReduxStore.Materials.Data.IMaterial) => void
  incrementPresets: (materialTypeId: number) => void
}

type Props = OwnProps & StateProps & DispatchProps & DataloaderInfo

interface Point {
  x: number
  y: number
}

const MultilineTool: React.FunctionComponent<Props> = props => {
  const {
    width,
    height,
    descriptions,
    dispatchAddMaterials,
    incrementPresets,
    selectedDrawing,
    selectedProject,
    selectedMaterialType,
    drawings,
    scale,
    autoIncrementEnabled,
    options,
    snappingEnabled,
    snappingDistance,
  } = props

  // State for multiline drawing
  const [isDrawing, setIsDrawing] = useState(false)
  const [points, setPoints] = useState<number[]>([])
  const [overlayId, setOverlayId] = useState<string | null>(null)
  const [finishButtonVisible, setFinishButtonVisible] = useState(false)
  
  // No need for panning state - the Zoomable component handles it
  
  // Get stroke width from options
  const strokeWidth = options && typeof options.strokeWidth === 'number'
    ? options.strokeWidth
    : 10
  
  // Convert event coordinates to canvas coordinates
  const getPointerPosition = (evt: Konva.KonvaEventObject<MouseEvent>): Point | null => {
    const stage = evt.target.findAncestor('Stage') as Konva.Stage
    if (!stage) return null
    
    const pos = stage.getPointerPosition()
    if (!pos) return null
    
    const transform = evt.target.getAbsoluteTransform().copy()
    transform.invert()
    const transformedPos = transform.point(pos)
    
    return {
      x: round(transformedPos.x, 2),
      y: round(transformedPos.y, 2)
    }
  }
  
  // Get snapping edges for the current position
  const getSnappingEdges = (pos: Point) => {
    if (!pos) return null
    
    return {
      vertical: [
        {
          guide: Math.round(pos.x),
          offset: 0,
          snap: 'start',
        },
      ],
      horizontal: [
        {
          guide: Math.round(pos.y),
          offset: 0,
          snap: 'start',
        },
      ],
    }
  }
  
  // Apply snapping to position if enabled
  const applySnapping = (evt: Konva.KonvaEventObject<MouseEvent>, pos: Point): Point => {
    if (!snappingEnabled) return pos
    
    const lineGuideStops = getLineGuideStops(evt.target, pos, snappingDistance)
    const itemBounds = getSnappingEdges(pos)
    
    if (itemBounds) {
      const guides = getGuides(lineGuideStops, itemBounds)
      const { presetX, presetY } = getAnchorPoints(guides)
      
      if (presetX || presetY) {
        drawGuides(guides, evt.target)
        return {
          x: presetX || pos.x,
          y: presetY || pos.y
        }
      }
    }
    
    return pos
  }
  
  // Handle mouse down event
  const handleMouseDown = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Ignore middle mouse button (button = 1) for panning
    if (evt.evt.button === 1) {
      // Let the event propagate to allow for panning
      return;
    }
    
    evt.evt.stopPropagation()
    
    // Only handle left mouse button for drawing
    if (evt.evt.button !== 0) return
    
    const rawPos = getPointerPosition(evt)
    if (!rawPos) return
    
    // Apply snapping
    const pos = applySnapping(evt, rawPos)
    
    if (!isDrawing) {
      // Start a new multiline
      console.log('Starting new multiline at', pos)
      const id = randomId()
      setOverlayId(id)
      setIsDrawing(true)
      setPoints([pos.x, pos.y, pos.x, pos.y])
      setFinishButtonVisible(true)
    } else {
      // Add a new point to the existing multiline
      console.log('Adding point to multiline at', pos)
      // Create a copy of the current points
      const newPoints = [...points]
      // Update the last point (which is the preview point)
      newPoints[newPoints.length - 2] = pos.x
      newPoints[newPoints.length - 1] = pos.y
      // Add a new preview point at the same position
      newPoints.push(pos.x, pos.y)
      setPoints(newPoints)
    }
  }
  
  // Handle mouse move event
  const handleMouseMove = throttleRaf((evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Skip processing for middle mouse button events
    if (evt.evt.buttons === 4) { // 4 corresponds to middle button being pressed
      return;
    }
    
    // If we're drawing, update the preview point
    if (isDrawing && points.length >= 4) {
      const rawPos = getPointerPosition(evt)
      if (!rawPos) return
      
      // Clear existing guide lines
      evt.target
        .getLayer()
        .find('.guid-line')
        .each((node: Konva.Node) => node.destroy())
      
      // Apply snapping
      const pos = applySnapping(evt, rawPos)
      
      // Update only the last point (preview point)
      const newPoints = [...points]
      newPoints[newPoints.length - 2] = pos.x
      newPoints[newPoints.length - 1] = pos.y
      setPoints(newPoints)
    }
  })
  
  // Handle mouse up event
  const handleMouseUp = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Only process if not middle mouse button
    if (evt.evt.button === 1) {
      return;
    }
  }
  
  // Complete the multiline and create the material
  const finishDrawing = () => {
    if (!isDrawing || points.length < 4) {
      console.warn('Cannot finish drawing - not enough points')
      return
    }
    
    console.log('Finishing multiline with', points.length / 2, 'points')
    
    // Remove the last point which is the preview point
    const finalPoints = points.slice(0, points.length - 2)
    
    if (autoIncrementEnabled && selectedMaterialType) {
      incrementPresets(selectedMaterialType as number)
    }
    
    // Find the drawing
    const drawing = drawings.find(d => d.displayName === selectedDrawing)
    if (!drawing) {
      console.error('Could not find drawing', selectedDrawing)
      return
    }
    
    // Create the material object
    const materialId = overlayId as string
    const material = {
      overlay_id: materialId,
      material_id: materialId,
      drawing_id: drawing.id,
      display_name: selectedDrawing as string,
      shape: 'line', // Using line shape type for multiline
      shape_data: JSON.stringify({
        points: finalPoints,
        strokeWidth: strokeWidth
      }),
      company_project_id: selectedProject as number,
      material_type_id: selectedMaterialType as number,
      value: descriptions.reduce((acc: { [key: string]: string }, name: string) => {
        if (selectedDrawing && name === 'Drawing Name') {
          acc[name] = selectedDrawing
        } else {
          // Check for presets
          const preset = props.presets.find(p => p.description === name)
          if (preset) {
            acc[name] = preset.value
          } else {
            const descData = props.descriptionsData.find(d => d.description === name)
            if (descData && 
               (descData.type === 'list' || descData.type === 'button-list') && 
               descData.choices.length > 0) {
              acc[name] = descData.choices[0]
            } else {
              acc[name] = ''
            }
          }
        }
        return acc
      }, {})
    }
    
    // Dispatch the action to add the material
    console.log('Dispatching material:', material)
    dispatchAddMaterials(material)
    
    // Reset drawing state
    setIsDrawing(false)
    setPoints([])
    setOverlayId(null)
    setFinishButtonVisible(false)
  }
  
  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDrawing) {
        if (e.key === 'Escape') {
          // Cancel drawing
          setIsDrawing(false)
          setPoints([])
          setOverlayId(null)
          setFinishButtonVisible(false)
        } else if (e.key === 'Enter' && points.length >= 4) {
          // Finish drawing
          finishDrawing()
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDrawing, points, overlayId])
  
  return (
    <Group onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="rgba(0, 0, 0, 0)"
        onMouseDown={handleMouseDown}
      />
      
      {/* Preview the multiline while drawing - with pointer events disabled to allow clicks through to canvas */}
      {isDrawing && points.length >= 4 && (
        <Line
          points={points}
          stroke="red"
          strokeWidth={strokeWidth}
          dash={[5, 5]}
          lineCap="square"
          lineJoin="miter"
          strokeScaleEnabled={false}
          listening={false} // Critical: Disable mouse events on the preview line
        />
      )}
      
      {/* Finish button */}
      {finishButtonVisible && (
        <Group
          x={10}
          y={10}
          onClick={finishDrawing}
        >
          <Rect
            width={120}
            height={40}
            fill="rgba(0, 100, 255, 0.7)"
            cornerRadius={5}
            shadowColor="black"
            shadowBlur={5}
            shadowOpacity={0.3}
            shadowOffset={{ x: 2, y: 2 }}
          />
          <Line
            points={[30, 20, 90, 20]}
            stroke="white"
            strokeWidth={3}
            lineCap="round"
          />
          <Line
            points={[70, 10, 90, 20, 70, 30]}
            stroke="white"
            strokeWidth={3}
            lineCap="round"
            lineJoin="round"
          />
        </Group>
      )}
    </Group>
  )
}

const mapStateToProps = (
  state: ReduxStore.State,
  { selectedMaterialType }: { selectedMaterialType: string }
) => {
  const multilineOptions = state.tool.options['multiline'] || { strokeWidth: 10 }
  
  return {
    descriptions: getDescriptions(state),
    descriptionsData: getDescriptionsData(state),
    drawings: getDrawings(state),
    snappingEnabled: getSnappingEnabled(state),
    presets: getMaterialTypePresets(state, selectedMaterialType),
    autoIncrementEnabled: getAutoIncrementPresetsEnabled(state),
    snappingDistance: getSnappingDistance(state),
    options: multilineOptions
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  dispatchAddMaterials: (m: ReduxStore.Materials.Data.IMaterial) => dispatch(action.addMaterial(m)),
  incrementPresets: (materialTypeId: number) => dispatch(incrementMaterialPresets(materialTypeId)),
})

export default withSelectedValues(
  // @ts-ignore
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(MultilineTool)
)
